import * as styles from "./ClientEditorDialog.css";

import { useAtomValue } from "jotai";
import type { JSX } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@frontend/components/atoms/Dialog";
import {
  ScrollArea,
  ScrollAreaContent,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";
import { useDiscardGuard } from "@frontend/hooks/useDiscardGuard";
import { DiscardChangesDialog } from "@frontend/shared/discard-changes-dialog";
import { Trans } from "@lingui/react/macro";

import { ClientEditorBody } from "./components/ClientEditorBody";
import { ClientEditorHeader } from "./components/ClientEditorHeader";
import {
  type UseClientEditorOptions,
  useClientEditor,
} from "./hooks/useClientEditor";
import { useShellTitle } from "./hooks/useShellTitle";
import { conditionsForm } from "./stores/conditions";
import { clientForm } from "./stores/editor";

export type ClientEditorDialogProps = UseClientEditorOptions & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ClientEditorDialog({
  open,
  onOpenChange,
  clientId = null,
  onSaved,
  onCancel,
}: ClientEditorDialogProps): JSX.Element {
  const title = useShellTitle(clientId);

  const clientDirty = useAtomValue(clientForm.state.isDirty);
  const conditionsDirty = useAtomValue(conditionsForm.state.isDirty);

  const { loading, saving, error, conflict, save, cancel } = useClientEditor({
    clientId,
    onSaved: (savedId) => {
      onOpenChange(false);
      onSaved?.(savedId);
    },
    onCancel: () => {
      onOpenChange(false);
      onCancel?.();
    },
  });

  // Whatever way out is taken, a dirty form asks first. The terms are a form
  // of their own, and unsaved work on them counts the same.
  const guard = useDiscardGuard({
    dirty: clientDirty || conditionsDirty,
    onClose: cancel,
  });

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange}>
      <DialogContent size="xl" className={styles.popup} aria-label={title}>
        {/* The client itself is the title bar: what the dialog is about,
            where it stands. */}
        <DialogHeader>
          <ClientEditorHeader variant="bare" />

          <DialogClose />
        </DialogHeader>

        <ScrollArea className={styles.scrollArea}>
          <ScrollAreaViewport fade="end">
            {/* Base UI writes a fit-content min-width inline: left alone, the
                widest row would widen the whole body. */}
            <ScrollAreaContent
              className={styles.scroller}
              style={{ minWidth: 0 }}
            >
              <ClientEditorBody
                loading={loading}
                error={error}
                conflict={conflict}
                stickyTabs
              />
            </ScrollAreaContent>
          </ScrollAreaViewport>

          <ScrollAreaScrollbar expandOnHover>
            <ScrollAreaThumb />
          </ScrollAreaScrollbar>
        </ScrollArea>

        <DialogFooter>
          <Button variant="neutral" onClick={guard.requestClose}>
            <Trans>Cancel</Trans>
          </Button>

          <Button
            variant="primary"
            loading={saving}
            disabled={loading || saving}
            shortcut="mod+s"
            onClick={() => void save()}
          >
            <Trans>Save</Trans>
          </Button>
        </DialogFooter>

        <DiscardChangesDialog
          open={guard.confirming}
          onKeep={guard.keep}
          onDiscard={guard.discard}
        />
      </DialogContent>
    </Dialog>
  );
}
